import React, {useState} from 'react';
import {Tab, Tabs, Box} from '@mui/material';
import {TourDrawer} from '../../components/TourDrawer';
import Typography from '@mui/material/Typography';
import {
  TourPreviewMarkers,
  TourPreviewMarkersProps,
} from './TourPreviewMarkers';
import {useAuthContext} from '../../../../context/AuthContext';
import {InspectionPanel} from '../../../propertyInspection';

interface AudioSubmission {
  id: string;
  videoURI?: string | null;
  originalFilename?: string | null;
  submissionStatus: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED';
  rawTranscript?: string | null;
  durationSeconds?: number | null;
  errorMessage?: string | null;
  createdAt?: string | null;
}

interface Defect {
  id: string;
  title: string;
  description?: string | null;
  severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  defectStatus:
    | 'IDENTIFIED'
    | 'IN_REVIEW'
    | 'ACCEPTED'
    | 'RESOLVED'
    | 'REJECTED';
  roomType?: string | null;
  defectCategory?: string | null;
  sourceClipIndex?: number | null;
  sourceFramePosition?: string | null;
  aiDetected: boolean;
  aiConfidence?: number | null;
  evidenceImageURI?: string | null;
  audioStartSeconds?: number | null;
  audioEndSeconds?: number | null;
}

interface Inspection {
  id: string;
  title: string;
  inspectionStatus: 'DRAFT' | 'IN_PROGRESS' | 'COMPLETED' | 'ARCHIVED';
  inspectionDate?: string | null;
  propertyAddress?: string | null;
  inspectorName?: string | null;
  defects?: Defect[];
  audioSubmissions?: AudioSubmission[];
}

interface TourPreviewDrawerProps extends TourPreviewMarkersProps {
  liveViewing: boolean;
  drawerIsOpen: boolean;
  keepDrawerOpen: boolean;
  setFastTravellingStatus: React.Dispatch<
    React.SetStateAction<'idle' | 'forward' | 'backward'>
  >;
  setDrawerIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  drawerRef: React.RefObject<HTMLDivElement>;
  // Inspect tab props (only used when isSuperAdmin)
  tourId?: string;
  inspections?: Inspection[];
  currentClipIndex?: number;
  refetch?: () => void;
}

export function TourPreviewDrawer({
  drawerIsOpen,
  liveViewing,
  keepDrawerOpen,
  setDrawerIsOpen,
  setFastTravellingStatus,
  drawerRef,
  tourId,
  inspections,
  currentClipIndex,
  refetch,
  ...markersProps
}: TourPreviewDrawerProps) {
  const {isSuperAdmin} = useAuthContext();
  const showInspect = isSuperAdmin();
  const [activeTab, setActiveTab] = useState(0);

  return (
    <TourDrawer
      liveViewing={liveViewing}
      drawerIsOpen={drawerIsOpen}
      keepDrawerOpen={keepDrawerOpen}
      open={drawerIsOpen}
      setOpen={setDrawerIsOpen}
      drawerRef={drawerRef}
    >
      {showInspect ? (
        <>
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            variant="fullWidth"
            sx={{mb: 2}}
          >
            <Tab label="Fast Travel" />
            <Tab label="Inspect" />
          </Tabs>

          {activeTab === 0 && (
            <>
              <Typography variant="h2" mb={2}>
                Fast Travel
              </Typography>
              <TourPreviewMarkers
                setFastTravellingStatus={setFastTravellingStatus}
                {...markersProps}
              />
            </>
          )}

          {activeTab === 1 && tourId && inspections !== undefined && refetch && (
            <Box sx={{height: '100%', overflow: 'auto', pt: 1}}>
              <InspectionPanel
                tourId={tourId}
                inspections={inspections}
                currentClipIndex={currentClipIndex ?? 0}
                refetch={refetch}
              />
            </Box>
          )}
        </>
      ) : (
        <>
          <Typography variant="h2" mb={2}>
            Fast Travel
          </Typography>
          <TourPreviewMarkers
            setFastTravellingStatus={setFastTravellingStatus}
            {...markersProps}
          />
        </>
      )}
    </TourDrawer>
  );
}
